import { useState } from 'react';
import {
  Chip, Stack, Button, Alert, Snackbar, IconButton, Tooltip, Typography, Divider, Box, Link,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { CheckCircle, Cancel, Payments, Visibility, Undo } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { epcPaymentApi, type EpcPayment } from '../../api/epcBilling.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
/** Human turnaround from ms (e.g. 2d 3h 15m). */
const fmtTat = (ms: number): string => {
  if (ms < 0) return '—';
  const m = Math.floor(ms / 60000), d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), mm = m % 60;
  return [d && `${d}d`, h && `${h}h`, (mm || (!d && !h)) && `${mm}m`].filter(Boolean).join(' ');
};
const stageColor = (s?: string | null): 'default' | 'info' | 'success' | 'error' | 'warning' =>
  s === 'done' ? 'success' : s === 'finance' ? 'info' : s === 'business_head' ? 'warning' : s === 'rom' ? 'info' : 'default';
const statusColor = (s: string): 'default' | 'info' | 'success' | 'error' | 'warning' =>
  s === 'paid' ? 'success' : s === 'rejected' ? 'error' : 'default';
const FILTERS = [
  { key: 'awaiting', label: 'Awaiting action' }, { key: 'submitted', label: 'In approval' },
  { key: 'paid', label: 'Paid' }, { key: 'rejected', label: 'Rejected' }, { key: '', label: 'All' },
];

/** EPC payment-approval queue: submitted → approve/reject → record payment. Only EPC payments. */
export function EpcPaymentApprovalsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filter, setFilter] = useState('awaiting');
  const [toast, setToast] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);

  const statusParam = filter === 'awaiting' ? undefined : filter || undefined;
  const { data, isLoading } = useQuery({
    queryKey: ['epc-payment-approvals', page, limit, filter],
    queryFn: () => epcPaymentApi.list({ page, limit: filter === 'awaiting' ? 200 : limit, status: statusParam }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['epc-payment-approvals'] });
  const act = (fn: () => Promise<unknown>, msg: string) => fn().then(() => { invalidate(); setToast(msg); }).catch((e) => setToast(extractApiError(e)));

  const approveMut = (id: number) => act(() => epcPaymentApi.approve(id), 'Approved');
  const rejectMut = (id: number) => { const r = window.prompt('Rejection reason?'); if (r) act(() => epcPaymentApi.reject(id, r), 'Rejected'); };
  const payMut = (id: number) => { const ref = window.prompt('Payment reference (optional)?') ?? undefined; act(() => epcPaymentApi.pay(id, { paymentRef: ref }), 'Payment recorded'); };

  let rows = data?.data ?? [];
  if (filter === 'awaiting') rows = rows.filter((r) => r.status === 'submitted');

  const columns: Column<EpcPayment>[] = [
    { key: 'paymentCode', label: 'Request', render: (r) => r.paymentCode },
    { key: 'payeeName', label: 'Payee', render: (r) => <span>{r.payeeName ?? '—'}<br /><small style={{ color: '#888' }}>{r.workOrderId ? `WO #${r.workOrderId}` : r.purchaseOrderId ? `LPO #${r.purchaseOrderId}` : r.payeeType}</small></span> },
    { key: 'paymentType', label: 'Type', render: (r) => <span>{r.paymentType}{r.paymentMode ? <><br /><small style={{ color: '#888' }}>{r.paymentMode}</small></> : null}</span> },
    { key: 'amount', label: 'Amount', align: 'right', render: (r) => inr(r.amount) },
    { key: 'stage', label: 'Stage', render: (r) => (
      r.status === 'submitted'
        ? <Chip size="small" label={r.stageLabel ?? r.stage} color={stageColor(r.stage)} />
        : <Chip size="small" label={r.status} color={statusColor(r.status)} />
    ) },
  ];

  return (
    <>
      <PageHeader title="EPC Payment Approvals" subtitle="Backend Finance → ROM → Business Head → Finance (transfer). Each approval advances the request to the next stage." />
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {FILTERS.map((f) => <Button key={f.key || 'all'} size="small" variant={filter === f.key ? 'contained' : 'outlined'} onClick={() => { setFilter(f.key); setPage(1); }}>{f.label}</Button>)}
      </Stack>

      <DataTable columns={columns} rows={rows} loading={isLoading} page={page} limit={limit} total={filter === 'awaiting' ? rows.length : (data?.meta.total ?? 0)}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="View"><IconButton size="small" onClick={() => setDetailId(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
            {r.status === 'submitted' && r.stage !== 'finance' && hasPermission('epc_payment.approve') && (
              <>
                <Tooltip title={`Approve (${r.stageLabel ?? r.stage}) → next stage`}><IconButton size="small" color="success" onClick={() => approveMut(r.id)}><CheckCircle fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => rejectMut(r.id)}><Cancel fontSize="small" /></IconButton></Tooltip>
              </>
            )}
            {r.status === 'submitted' && r.stage === 'finance' && hasPermission('epc_payment.pay') && (
              <>
                <Tooltip title="Finance: approve & transfer"><IconButton size="small" color="primary" onClick={() => payMut(r.id)}><Payments fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => rejectMut(r.id)}><Cancel fontSize="small" /></IconButton></Tooltip>
              </>
            )}
            {(r.status === 'submitted' || r.status === 'rejected') && hasPermission('epc_payment.update') && (
              <Tooltip title="Revise (send back to draft)"><IconButton size="small" color="warning" onClick={() => act(() => epcPaymentApi.revise(r.id), 'Sent back for revision')}><Undo fontSize="small" /></IconButton></Tooltip>
            )}
          </Stack>
        )} />

      {detailId && <ApprovalDetailDialog id={detailId} onClose={() => setDetailId(null)} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function DetailBlock({ title, lines }: { title: string; lines: Array<string | null | undefined | false> }) {
  const items = lines.filter(Boolean) as string[];
  return (
    <Box sx={{ minWidth: 200, flex: '1 1 200px' }}>
      <Typography variant="overline" color="text.secondary">{title}</Typography>
      {items.length ? items.map((l, i) => <Typography key={i} variant="caption" display="block">{l}</Typography>) : <Typography variant="caption" color="text.secondary">—</Typography>}
    </Box>
  );
}

function ApprovalDetailDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const { data: p } = useQuery({ queryKey: ['epc-payment', id], queryFn: () => epcPaymentApi.get(id) });
  const srcType = p?.workOrderId ? 'work_order' : p?.purchaseOrderId ? 'local_purchase' : null;
  const srcId = p?.workOrderId ?? p?.purchaseOrderId ?? null;
  const { data: details } = useQuery({
    queryKey: ['epc-pay-source', srcType, srcId],
    queryFn: () => epcPaymentApi.sourceDetails(srcType as 'work_order' | 'local_purchase', Number(srcId)),
    enabled: Boolean(srcType && srcId),
  });
  if (!p) return null;
  const history = p.stageHistory ?? [];
  const overallTat = history.length >= 2 ? new Date(history[history.length - 1].at).getTime() - new Date(history[0].at).getTime() : 0;
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{p.paymentCode}
        {p.status === 'submitted' ? <Chip size="small" sx={{ ml: 1 }} label={p.stageLabel ?? 'In approval'} color={stageColor(p.stage)} /> : <Chip size="small" sx={{ ml: 1 }} label={p.status} color={statusColor(p.status)} />}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={0.5}>
          <Typography variant="body2"><b>Payment:</b> {p.paymentType}{p.paymentMode ? ` · ${p.paymentMode}` : ''} · {inr(p.amount)}</Typography>
          <Typography variant="body2"><b>Source:</b> {details?.orderCode ?? (p.workOrderId ? `Work Order #${p.workOrderId}` : p.purchaseOrderId ? `Local Purchase #${p.purchaseOrderId}` : '—')}{details ? ` · order value ${inr(details.orderValue)}` : ''}</Typography>
          {p.remarks && p.status !== 'rejected' && <Typography variant="body2"><b>Remarks:</b> {p.remarks}</Typography>}
          {p.attachmentUrl && <Typography variant="body2"><b>Attachment:</b> <Link href={p.attachmentUrl} target="_blank" rel="noreferrer">View</Link></Typography>}
          {p.status === 'rejected' && p.remarks && <Alert severity="warning" sx={{ mt: 1 }}>Rejected: {p.remarks}</Alert>}
        </Stack>

        {details && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={3} rowGap={1.5} flexWrap="wrap" useFlexGap>
              {details.client && <DetailBlock title="Client" lines={[details.client.name, details.client.gstNumber && `GST: ${details.client.gstNumber}`, details.client.address, [details.client.contact, details.client.mobile].filter(Boolean).join(' · ')]} />}
              {details.project && <DetailBlock title="Project" lines={[`${details.project.name} (${details.project.code})`]} />}
              {details.site && <DetailBlock title="Site" lines={[`${details.site.name} (${details.site.code})`, details.site.address]} />}
              {details.vendor && <DetailBlock title="Vendor" lines={[details.vendor.name, details.vendor.gstNumber && `GST: ${details.vendor.gstNumber}`, details.vendor.panNumber && `PAN: ${details.vendor.panNumber}`, details.vendor.address, (details.vendor.bankName || details.vendor.bankAccount || details.vendor.ifsc) && `Bank: ${[details.vendor.bankName, details.vendor.bankAccount, details.vendor.ifsc].filter(Boolean).join(' / ')}`, [details.vendor.mobile, details.vendor.email].filter(Boolean).join(' · ')]} />}
              {!details.vendor && details.shop && <DetailBlock title="Shop / Supplier" lines={[details.shop.name, details.shop.gstNumber && `GST: ${details.shop.gstNumber}`, details.shop.address, [details.shop.contact, details.shop.mobile].filter(Boolean).join(' · ')]} />}
            </Stack>
          </>
        )}
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="overline" color="text.secondary">Approval trail{overallTat > 0 ? ` · total TAT ${fmtTat(overallTat)}` : ''}</Typography>
        <Stack spacing={0.75} sx={{ mt: 0.5 }}>
          {history.length === 0 && <Typography variant="caption" color="text.secondary">Not submitted yet.</Typography>}
          {history.map((h, i) => {
            const tat = i > 0 ? new Date(h.at).getTime() - new Date(history[i - 1].at).getTime() : 0;
            return (
              <Stack key={i} direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={`${h.stageLabel ?? h.stage} · ${h.action}`} color={h.action === 'rejected' ? 'error' : h.action === 'paid' ? 'success' : 'default'} />
                <Typography variant="caption" color="text.secondary">
                  {h.byName ?? '—'} · {new Date(h.at).toLocaleString('en-IN')}{i > 0 ? ` · took ${fmtTat(tat)}` : ''}{h.remarks ? ` · ${h.remarks}` : ''}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
